// TermsAndConditions.tsx
import React from "react";
import "./css/TermsAndConditions.css";

export const TermsAndConditions: React.FC = () => {
    return (
        <div className="terms">
            <div className="terms__container">
                <p className="terms__last-updated">
                    Last updated: {new Date().toLocaleDateString("en-GB")}
                </p>

                <section className="terms__section">
                    <h2>1. Introduction</h2>
                    <p>
                        These Terms and Conditions (“Terms”) govern your access to and use
                        of the Skitterbook web application (“Skitterbook”, “the Service”),
                        operated by FAR Authoring Ltd. By accessing or using Skitterbook,
                        you agree to be bound by these Terms.
                    </p>
                    <p>
                        If you do not agree with these Terms, you must not use the Service.
                    </p>
                </section>

                <section className="terms__section">
                    <h2>2. Who we are</h2>
                    <p>
                        Skitterbook is operated by FAR Authoring Ltd., registered in the
                        United Kingdom.
                    </p>
                    <p>
                        <strong>Company:</strong> FAR Authoring Ltd.
                        <br />
                        <strong>Address:</strong> 41 Grange Park, Ealing, London, W5 3PR,
                        United Kingdom
                        <br />
                        <strong>Email:</strong>{" "}
                        <a href="mailto:farauthoring@gmail.com">
                            farauthoring@gmail.com
                        </a>
                    </p>
                </section>

                <section className="terms__section">
                    <h2>3. Use of the Service</h2>
                    <p>
                        Skitterbook allows users to upload short videos and images to
                        generate flipbooks and store them in a personal library. You agree
                        to use the Service only for lawful purposes and in accordance with
                        these Terms.
                    </p>
                    <p>You must not:</p>
                    <ul>
                        <li>Use the Service for any illegal or harmful activity.</li>
                        <li>
                            Attempt to access accounts, data or systems that do not belong to
                            you.
                        </li>
                        <li>
                            Upload content that infringes the rights of others, including
                            copyright or privacy rights.
                        </li>
                        <li>
                            Interfere with or disrupt the operation, security or performance
                            of the Service.
                        </li>
                    </ul>
                </section>

                <section className="terms__section">
                    <h2>4. Account registration</h2>
                    <p>
                        To use certain features of Skitterbook, you must create an account
                        by providing a given name, email address and password. You are
                        responsible for maintaining the confidentiality of your login
                        details and for all activity under your account.
                    </p>
                    <p>
                        You agree to notify us immediately if you suspect unauthorised use
                        of your account.
                    </p>
                </section>

                <section className="terms__section">
                    <h2>5. User‑generated content</h2>
                    <p>
                        You may upload short videos and images to Skitterbook for the
                        purpose of generating flipbooks. You retain full ownership of all
                        content you upload.
                    </p>
                    <p>
                        Uploaded content is private to you and cannot be viewed by other
                        users or by system managers during normal operation of the Service.
                    </p>
                    <p>
                        By uploading content, you grant Skitterbook a limited licence to
                        store, process and display your content solely for the purpose of
                        providing the Service to you.
                    </p>
                </section>

                <section className="terms__section">
                    <h2>6. Availability and changes to the Service</h2>
                    <p>
                        We aim to provide a reliable and high‑quality experience, but we do
                        not guarantee that the Service will always be available, error‑free
                        or uninterrupted.
                    </p>
                    <p>
                        We may update, modify or discontinue parts of the Service at any
                        time to improve performance, security or functionality.
                    </p>
                </section>

                <section className="terms__section">
                    <h2>7. Cookies and data usage</h2>
                    <p>
                        Skitterbook uses cookies to store session details, application
                        settings and performance‑related information. Anonymous analytics
                        data is collected through Azure Application Insights to help us
                        improve the Service.
                    </p>
                    <p>
                        For full details on how we handle your data, please refer to our{" "}
                        <a href="/privacy">Privacy Policy</a>.
                    </p>
                </section>

                <section className="terms__section">
                    <h2>8. Prohibited behaviour</h2>
                    <p>You must not use Skitterbook to:</p>
                    <ul>
                        <li>Upload unlawful, harmful or abusive content.</li>
                        <li>Attempt to reverse‑engineer or copy the Service.</li>
                        <li>Introduce malware, viruses or harmful code.</li>
                        <li>
                            Circumvent security measures or attempt unauthorised access to
                            systems.
                        </li>
                    </ul>
                </section>

                <section className="terms__section">
                    <h2>9. Intellectual property</h2>
                    <p>
                        All intellectual property rights in the Skitterbook platform,
                        including software, design, branding and content (excluding
                        user‑generated content), are owned by FAR Authoring Ltd.
                    </p>
                    <p>
                        You may not copy, modify, distribute or create derivative works
                        based on any part of the Service unless expressly permitted.
                    </p>
                </section>

                <section className="terms__section">
                    <h2>10. Limitation of liability</h2>
                    <p>
                        Skitterbook is provided “as is” without warranties of any kind. To
                        the fullest extent permitted by law, FAR Authoring Ltd. is not
                        liable for:
                    </p>
                    <ul>
                        <li>Loss of data, profits or business opportunities.</li>
                        <li>Service interruptions or technical issues.</li>
                        <li>Unauthorised access resulting from your actions.</li>
                        <li>
                            Any indirect, incidental or consequential damages arising from
                            your use of the Service.
                        </li>
                    </ul>
                </section>

                <section className="terms__section">
                    <h2>11. Termination</h2>
                    <p>
                        We may suspend or terminate your access to the Service if you
                        violate these Terms or if continued access poses a security or
                        operational risk.
                    </p>
                    <p>
                        You may delete your account at any time. Upon deletion, your
                        personal data and uploaded content will be removed in accordance
                        with our Privacy Policy.
                    </p>
                </section>

                <section className="terms__section">
                    <h2>12. Changes to these Terms</h2>
                    <p>
                        We may update these Terms from time to time. When changes are made,
                        we will update the “Last updated” date at the top of this page.
                    </p>
                    <p>
                        Continued use of Skitterbook after changes are published constitutes
                        acceptance of the updated Terms.
                    </p>
                </section>

                <section className="terms__section">
                    <h2>13. Governing law</h2>
                    <p>
                        These Terms are governed by the laws of England and Wales. Any
                        disputes arising from these Terms or your use of the Service will be
                        subject to the exclusive jurisdiction of the courts of England and
                        Wales.
                    </p>
                </section>

                <section className="terms__section terms__disclaimer">
                    <p>
                        These Terms are provided for general informational purposes and may
                        benefit from independent legal review to ensure compliance with your
                        specific obligations.
                    </p>
                </section>
            </div>
        </div>
    );
};

export default TermsAndConditions;
